import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { QueuesService } from '../../_services/queues.service';
import { QueuesMembersService } from '../../_services/queues-members.service';
import { QueuesDetailComponent } from '../queues-detail/queues-detail.component';
import { LoginService } from 'src/app/modules/login/login.service';
import { ContractsService } from 'src/app/modules/companies/_services/contracts.service';

@Component({
    selector: 'app-queues-list',
    templateUrl: './queues-list.component.html',
    styleUrls: ['./queues-list.component.css'],
    host: { class: 'list-page' },
    standalone: false
})
export class QueuesListComponent implements OnInit {
  public queues: any[] = [];
  public totalRecords: number = 0;
  public loading: boolean = false;
  private lastGridEvent: any;
  public filters: any = {
    name: new Filter(FilterOperation.contains, 'name')
  };

  constructor(
    public topbarService: TopbarService,
    private queuesService: QueuesService,
    private queuesMembersService: QueuesMembersService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private loginService: LoginService,
    private contractsService: ContractsService
  ) { }

  private contractIds: Set<string> = new Set();

  ngOnInit() {
    this.topbarService.setTitle('Colas');
    this.topbarService.addAction("Nueva cola", "pi pi-plus", false, () => this.edit(null));
  }

  async loadData($event: any) {
    this.lastGridEvent = $event;
    this.loading = true;

    let params: IParamsCollection = {
      page: { number: $event.first / $event.rows + 1, size: $event.rows },
      sort: ['name'],
    };

    if (typeof $event.sortField != 'undefined') {
      params.sort = [($event.sortOrder > 0 ? '' : '-') + $event.sortField];
    }

    params.filter = FilterBuilder.fromObject(this.filters).build();

    try {
      const response = await firstValueFrom(this.queuesService.all(params));
      this.queues = response.data;
      this.totalRecords = response.meta['total'];
      this.topbarService.setCount(this.totalRecords);
      await Promise.all([this.loadMemberCounts(), this.loadContractIds()]);
      this.queues.forEach((q: any) => {
        q.isFromContract = this.contractIds.has(q.id);
      });
    } finally {
      this.loading = false;
    }
  }

  private async loadContractIds() {
    if (this.contractIds.size > 0) return;
    const response = await firstValueFrom(this.contractsService.all({ page: { number: 1, size: 5000 } }));
    this.contractIds = new Set(response.data.map((c: any) => c.id));
  }

  private async loadMemberCounts() {
    if (!this.queues.length) return;
    const params: IParamsCollection = {
      page: { number: 1, size: 5000 },
    };
    const response = await firstValueFrom(this.queuesMembersService.all(params));
    const members: any[] = response.data;
    this.queues.forEach((q: any) => {
      q.memberCount = members.filter((m: any) => m.queueName === q.name).length;
    });
  }

  clearFilters() {
    this.filters.name.value = null;
    this.search();
  }

  search() {
    if (!this.lastGridEvent) return;
    this.lastGridEvent.first = 0;
    this.loadData(this.lastGridEvent);
  }

  edit(id: any) {
    const ref = this.dialogService.open(QueuesDetailComponent, {
      closable: true,
      modal: true,
      data: { id: id ? id : null },
      header: id ? 'Detalle de la cola' : 'Nueva cola',
      width: '900px'
    });
    ref.onClose.subscribe(() => {
      this.loadData(this.lastGridEvent);
    });
  }

  delete(queue: any) {
    if (queue.isFromContract) {
      this.messageService.add({
        closable: true,
        severity: 'warn',
        detail: 'No se puede eliminar la cola "' + queue.name + '" porque está gestionada por un contrato.'
      });
      return;
    }
    if (queue.memberCount > 0) {
      this.messageService.add({
        closable: true,
        severity: 'warn',
        detail: 'No se puede eliminar la cola "' + queue.name + '" porque tiene miembros asignados. Elimine los miembros primero.'
      });
      return;
    }
    this.confirmationService.confirm({
      message: '¿Está seguro que desea eliminar la cola "' + queue.name + '"?',
      header: 'Eliminar cola',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.queuesService.delete(queue.id).subscribe({
          next: () => {
            this.messageService.add({ closable: true, severity: 'success', detail: 'Cola eliminada correctamente' });
            this.loadData(this.lastGridEvent);
          },
          error: (error: any) => {
            this.messageService.add({ closable: true, severity: 'error', detail: error?.error?.title || 'Error al eliminar la cola' });
          }
        });
      }
    });
  }
}
